/**
 * RoiCard — the "investor slide" on every host stand.
 *
 * Translates raw call / SMS / seating volume into a dollar figure the
 * operator can screenshot and the founder can drop into a pitch deck.
 * Numbers are conservative by design so they survive an owner's eye-test.
 */
import { useEffect, useState, useCallback } from "react";
import { TrendUp, Phone, ChatCircleText, Users } from "@phosphor-icons/react";
import { api } from "../lib/api";

const fmt = (n) => "$" + (n || 0).toLocaleString();

export default function RoiCard() {
  const [roi, setRoi] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/analytics/roi?days=7");
      setRoi(r.data);
    } catch (e) { console.debug("roi load failed:", e?.message); }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);
  if (!roi) return null;

  const zero = roi.total_value === 0 && roi.calls_handled_by_ai === 0;
  return (
    <div className="paper-card p-5 mb-6 bg-[#1E3A2F] text-[#F7F6F2] relative overflow-hidden" data-testid="roi-card">
      <div className="absolute top-0 right-0 opacity-5 pointer-events-none"><TrendUp size={140} weight="fill" color="#F5C543" /></div>
      <div className="relative">
        <div className="overline text-[#F5C543] mb-1">This week · powered by BuzzerAI</div>
        {zero ? (
          <>
            <div className="text-3xl font-heading font-extrabold mb-1">Ready to prove itself</div>
            <div className="text-sm text-[#C2D1C1]">The AI's stats show up here the moment your first call lands. Send yourself a test call from Voice → Take a test call.</div>
          </>
        ) : (
          <>
            <div className="text-4xl sm:text-5xl font-heading font-extrabold mb-1" data-testid="roi-total">
              {fmt(roi.total_value)}<span className="text-lg text-[#F5C543] ml-2">saved & captured</span>
            </div>
            <div className="text-sm text-[#C2D1C1]">
              <span className="font-semibold text-[#F7F6F2]">{fmt(roi.labor_dollars_saved)}</span> in host labor
              {" · "}<span className="font-semibold text-[#F7F6F2]">{fmt(roi.revenue_captured)}</span> in captured covers
            </div>
          </>
        )}
        <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-[#F7F6F2]/15">
          <Stat icon={<Phone size={14} weight="fill" color="#F5C543" />} label="AI-answered calls" value={roi.calls_handled_by_ai} testid="roi-calls" />
          <Stat icon={<Users size={14} weight="fill" color="#F5C543" />} label="Guests seated via AI" value={roi.ai_guests_seated} testid="roi-seated" />
          <Stat icon={<ChatCircleText size={14} weight="fill" color="#F5C543" />} label="SMS delivered" value={roi.sms_sent} testid="roi-sms" />
        </div>
        <div className="text-[10px] text-[#C2D1C1]/70 mt-3">
          Assumes ${roi.assumptions.host_wage_per_hour}/hr host wage · {roi.assumptions.avg_call_handle_minutes}min avg call · ${roi.assumptions.avg_cover_dollars} avg cover. Conservative defaults you can tune in Settings.
        </div>
      </div>
    </div>
  );
}

const Stat = ({ icon, label, value, testid }) => (
  <div data-testid={testid}>
    <div className="text-2xl font-heading font-extrabold text-[#F7F6F2]">{value ?? 0}</div>
    <div className="text-[11px] text-[#C2D1C1] flex items-center gap-1 mt-0.5">{icon} {label}</div>
  </div>
);
